import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import MainLayout from '../layout/MainLayout/MainLayout'
import { IconAddWindow, IconPlus } from '../layout/NavRail/NavRail'
import NavLocation from '../layout/NavLocation/NavLocation'
import NavList from '../layout/NavList/NavList'
import {
  GlobalChatList,
  GlobalSearchWindowBody,
  globalChatLabel,
} from '../components/globalsearch'
import { WindowGrid, GridDock, useWindowGrid } from '../components/grid'
import { useGlobalChatCache } from '../hooks/useGlobalChatCache'
import { useModal } from '../context/ModalContext'
import { ItemBookmarkProvider } from '../components/references/ItemBookmarkContext'
import { deleteGlobalChat } from '../services/globalChats'
import { usePageTitle } from '../components/DocumentTitle'

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconEdit = () => (
  <svg {...svgProps}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
)

const IconDelete = () => (
  <svg {...svgProps}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
)

// Global Search workspace: a collapsible sidebar (chat list) beside a tiled
// window grid. Each window loads one chat (its own conversation + PDF viewer), so
// several chats can be open at once for comparison. Every question searches the
// entire library. Mirrors the Searchrooms page; a chat has no item scope, so the
// nav has no facet filter.
function GlobalSearchPage() {
  const { openModal } = useModal()
  const [reloadSignal, setReloadSignal] = useState(0)
  const [notice, setNotice] = useState('')

  const grid = useWindowGrid({
    storageKey: 'ct-grid-globalsearch-v1',
    limits: { maxWindows: 6, maxPerColumn: 2, maxColumns: 3 },
    initialType: 'globalchat',
  })

  // Full chat docs for every window that has one (fetched on reload, seeded on
  // selection). Empty windows (entityId null) contribute nothing to fetch.
  const entityIds = useMemo(
    () => grid.columns.flatMap((col) => col.windows.map((w) => w.entityId)).filter(Boolean),
    [grid.columns]
  )
  const { chatsById, seed, remove } = useGlobalChatCache(entityIds)

  // Sidebar highlight tracks the active window's chat.
  const activeChatId = useMemo(() => {
    for (const col of grid.columns) {
      const win = col.windows.find((w) => w.id === grid.activeId)
      if (win) return win.entityId
    }
    return null
  }, [grid.columns, grid.activeId])

  // Auto-dismiss the "too many windows" notice.
  useEffect(() => {
    if (!notice) return undefined
    const timer = setTimeout(() => setNotice(''), 4000)
    return () => clearTimeout(timer)
  }, [notice])

  // Load a chat into the ACTIVE window (the last-focused one). We already hold the
  // chat, so seed the cache to skip a fetch. Never creates a window.
  const openChat = useCallback(
    (chat) => {
      if (!chat?._id) return
      seed(chat)
      grid.selectEntity({ type: 'globalchat', entityId: chat._id })
    },
    [seed, grid]
  )
  const openChatRef = useRef(openChat)
  openChatRef.current = openChat

  // Add a new (empty) window to the grid, honoring the soft cap.
  const handleAddWindow = useCallback(() => {
    if (grid.isFull) {
      setNotice(
        `You can open up to ${grid.limits.maxWindows} windows at once. Close one to add another.`
      )
      return
    }
    grid.addWindow({ type: 'globalchat' })
  }, [grid])

  // Any window showing a deleted chat reverts to the empty state (kept, not
  // closed), mirroring Searchrooms.
  const clearWindowsForChat = useCallback(
    (chatId) => {
      grid.columns.forEach((col) =>
        col.windows.forEach((w) => {
          if (w.entityId === chatId) grid.setWindowEntity(w.id, null)
        })
      )
    },
    [grid]
  )

  const handleNewChat = () => {
    openModal('create-global-chat', {
      onCreated: (created) => {
        setReloadSignal((n) => n + 1)
        if (created) openChatRef.current(created)
      },
    })
  }

  const handleEditChat = useCallback(
    (chat) => {
      if (!chat) return
      openModal('edit-global-chat', {
        chat,
        onSaved: (updated) => {
          seed(updated)
          setReloadSignal((n) => n + 1)
        },
      })
    },
    [openModal, seed]
  )

  const handleDeleteChat = useCallback(
    (chat) => {
      if (!chat) return
      openModal('confirm', {
        title: 'Delete chat',
        message: `Delete "${globalChatLabel(chat)}"? This can't be undone.`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => deleteGlobalChat(chat._id),
        onConfirmed: () => {
          clearWindowsForChat(chat._id)
          remove(chat._id)
          setReloadSignal((n) => n + 1)
        },
      })
    },
    [openModal, clearWindowsForChat, remove]
  )

  const getTitle = useCallback(
    (win) => {
      if (!win.entityId) return 'New window'
      const chat = chatsById[win.entityId]
      return chat ? globalChatLabel(chat) : 'Loading…'
    },
    [chatsById]
  )
  usePageTitle(activeChatId ? getTitle({ entityId: activeChatId }) : '')

  const renderWindow = useCallback(
    (win) => {
      if (!win.entityId) {
        return <div className="display-empty">Select or create a chat.</div>
      }
      return (
        <GlobalSearchWindowBody
          chat={chatsById[win.entityId]}
          panes={win.panes}
          onPanesChange={(layout) => grid.setPanes(win.id, layout)}
        />
      )
    },
    [chatsById, grid]
  )

  // Rename/delete only make sense once a chat is loaded into the window. They live
  // in the window's "more" menu as labelled rows.
  const renderMenuItems = useCallback(
    (win) => {
      const chat = win.entityId ? chatsById[win.entityId] : null
      if (!chat) return null
      return (
        <>
          <button type="button" role="menuitem" onClick={() => handleEditChat(chat)}>
            <IconEdit />
            Edit chat
          </button>
          <button
            type="button"
            role="menuitem"
            className="is-danger"
            onClick={() => handleDeleteChat(chat)}
          >
            <IconDelete />
            Delete chat
          </button>
        </>
      )
    },
    [chatsById, handleEditChat, handleDeleteChat]
  )

  return (
    <ItemBookmarkProvider>
    <MainLayout
      storageId="global-search-layout"
      navLocation={<NavLocation title="Global AI Search" />}
      navActions={[
        {
          key: 'add-window',
          label: 'Add window',
          icon: <IconAddWindow />,
          onClick: handleAddWindow,
        },
        {
          key: 'new',
          label: 'New chat',
          icon: <IconPlus />,
          onClick: handleNewChat,
        },
      ]}
      navDock={
        <GridDock
          items={grid.minimized}
          getTitle={getTitle}
          onRestore={grid.restoreWindow}
          onClose={grid.closeWindow}
        />
      }
      nav={
        <NavList>
          <GlobalChatList
            reloadSignal={reloadSignal}
            selectedId={activeChatId}
            onSelect={openChat}
          />
        </NavList>
      }
      display={
        <div className="sr-grid-wrap">
          {notice && <div className="sr-grid-notice">{notice}</div>}
          <div className="sr-grid-body">
            <WindowGrid
              grid={grid}
              renderWindow={renderWindow}
              getTitle={getTitle}
              renderMenuItems={renderMenuItems}
              empty="Select or create a chat to get started."
            />
          </div>
        </div>
      }
    />
    </ItemBookmarkProvider>
  )
}

export default GlobalSearchPage
